import { CSSProperties, HTMLAttributes, useEffect, useRef, useState } from "react";

interface EditableTextProps extends Omit<HTMLAttributes<HTMLSpanElement>, "onChange"> {
  value: string;
  onChange: (newValue: string) => void;
  numeric?: boolean;
  disabled?: boolean;
  inputProps?: any;
}

const EditableText = ({
  value,
  numeric = false,
  disabled = false,
  children,
  onChange,
  inputProps = {},
  ...spanProps
}: EditableTextProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [spanWidth, setSpanWidth] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.select();
    }
  }, [isEditing]);

  const startEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (disabled) return;
    setInputValue(value);

    // Capture the width of the span before switching to edit mode
    if (spanRef.current) {
      const width = spanRef.current.offsetWidth;
      setSpanWidth(width);
    }

    setIsEditing(true);
  };

  const saveChanges = () => {
    const newValue = inputValue.replace(",", ".").trim();
    if (newValue !== value) {
      onChange(newValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<any>) => {
    if (e.key === "Enter") {
      saveChanges();
    } else if (e.key === "Escape") {
      setIsEditing(false);
      setInputValue(value);
    }
  };

  if (isEditing && !disabled) {
    const inputStyle: CSSProperties = spanWidth
      ? { minWidth: `${spanWidth}px`, maxWidth: `${spanWidth * 1.5}px` }
      : {};

    if (numeric) {
      return (
        <div className="overflow-hidden inline">
          <input
            type="text" // not "numeric" because ios safari sanitization.
            min={0}
            inputMode="decimal"
            pattern="[0-9\.,]*"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e)}
            onBlur={() => saveChanges()}
            autoFocus
            ref={inputRef}
            style={inputStyle}
            {...inputProps}
            className={`text-[16px] text-center px-1 field-sizing-content wrap-break-word placeholder:italic inline overflow-scroll rounded-sm bg-gray-100 text-black dark:bg-gray-900 dark:text-white outline-none ${
              spanProps.className ?? ""
            } ${inputProps.className ?? ""}`}
          />
        </div>
      );
    } else {
      return (
        <div className="overflow-hidden -mb-1.5">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e)}
            onSubmit={() => saveChanges()}
            onBlur={() => saveChanges()}
            autoFocus
            ref={inputRef}
            style={inputStyle}
            {...inputProps}
            className={`field-sizing-content wrap-break-word placeholder:italic inline overflow-scroll bg-yellow-500/20 outline-none ${
              spanProps.className ?? ""
            } ${inputProps.className ?? ""} normal-case`}
          />
        </div>
      );
    }
  }

  return (
    <span onClick={startEditing} ref={spanRef} {...spanProps}>
      {children ?? value}
    </span>
  );
};

export { EditableText };
